import { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AppHeader } from "../components/AppHeader";
import { apiFetch } from "../lib/api";
import { useMe } from "../hooks/useMe";
import { resolveMembership } from "../hooks/useFamilyMembership";
import { can } from "@family/shared";
import type { AlbumsListResponse, Album } from "@family/shared";

export function AlbumsPage() {
  const navigate = useNavigate();
  const meQuery = useMe();
  const primary = resolveMembership(meQuery.data);
  const slug = primary?.familySlug;

  const albumsQuery = useQuery<AlbumsListResponse>({
    queryKey: ["albums", slug],
    queryFn: () => apiFetch<AlbumsListResponse>(`/families/${slug}/albums`),
    enabled: Boolean(slug),
  });

  const [showCreate, setShowCreate] = useState(false);

  if (!primary || !slug) {
    return (
      <>
        <AppHeader />
        <main className="page"><p className="text-muted">Loading…</p></main>
      </>
    );
  }

  const albums = albumsQuery.data?.albums ?? [];
  const canCreate = can(primary.role, "post:create");

  return (
    <>
      <AppHeader />
      <main className="page stack-lg">
        <div className="row-between">
          <h1>Albums</h1>
          {canCreate && (
            <div style={{ display: "flex", gap: "var(--s-2)", flexWrap: "wrap" }}>
              {can(primary.role, "media:upload") && (
                <button type="button" className="btn-secondary" onClick={() => navigate("/scan")}>
                  📷 Scan old photos
                </button>
              )}
              <button type="button" onClick={() => setShowCreate(!showCreate)}>
                {showCreate ? "Cancel" : "+ New Album"}
              </button>
            </div>
          )}
        </div>

        <p className="text-muted" style={{ marginTop: "var(--s-2)" }}>
          Albums are for your old printed photo albums. Scan or photograph the
          pages and upload them here to preserve those memories and share them
          with the family.
        </p>

        {showCreate && (
          <CreateAlbumForm
            familySlug={slug}
            onCreated={() => setShowCreate(false)}
          />
        )}

        {albumsQuery.isLoading && <p className="text-muted">Loading…</p>}
        {albumsQuery.error && (
          <p className="text-error">{(albumsQuery.error as Error).message}</p>
        )}

        {!albumsQuery.isLoading && albums.length === 0 && (
          <p className="text-muted">
            No albums yet. Start one to digitize an old printed album and keep
            those photos safe and shared.
          </p>
        )}

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
            gap: "var(--s-3)",
          }}
        >
          {albums.map((album) => (
            <AlbumCard key={album.id} album={album} familySlug={slug} canDelete={can(primary.role, "events:manage")} />
          ))}
        </div>
      </main>
    </>
  );
}

function AlbumCard({ album, familySlug, canDelete }: { album: Album; familySlug: string; canDelete: boolean }) {
  const queryClient = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);

  const deleteAlbum = useMutation({
    mutationFn: () =>
      apiFetch(`/families/${familySlug}/albums/${album.id}`, { method: "DELETE" }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["albums", familySlug] });
    },
  });

  const setCover = useMutation({
    mutationFn: async (file: File) => {
      // Upload file, get mediaId, then set as cover
      const { uploadUrl, mediaId } = await apiFetch<{ uploadUrl: string; mediaId: string }>(
        `/families/${familySlug}/media/upload-url`,
        {
          method: "POST",
          body: JSON.stringify({ kind: "photo", mimeType: file.type, fileSize: file.size }),
        },
      );
      await fetch(uploadUrl, { method: "PUT", headers: { "Content-Type": file.type }, body: file });
      await apiFetch(`/families/${familySlug}/albums/${album.id}`, {
        method: "PATCH",
        body: JSON.stringify({ coverMediaId: mediaId }),
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["albums", familySlug] });
    },
  });

  return (
    <div
      style={{
        borderRadius: 8,
        overflow: "hidden",
        border: "1px solid var(--c-border)",
        background: "var(--c-bg)",
      }}
    >
      {/* Cover */}
      <Link
        to={`/albums/${album.id}`}
        style={{ display: "block", textDecoration: "none", color: "var(--c-text)" }}
      >
        <div
          style={{
            aspectRatio: "16 / 10",
            background: album.coverUrl
              ? `url(${album.coverUrl}) center / cover`
              : "var(--c-bg-alt)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {!album.coverUrl && (
            <span style={{ fontSize: "2rem", opacity: 0.3 }}>📷</span>
          )}
        </div>
      </Link>
      {/* Info */}
      <div style={{ padding: "12px 16px" }}>
        <Link to={`/albums/${album.id}`} style={{ textDecoration: "none", color: "var(--c-text)" }}>
          <strong style={{ fontSize: "0.95rem" }}>{album.title}</strong>
        </Link>
        {album.description && (
          <p className="text-muted" style={{ fontSize: "0.8rem", margin: "4px 0 0", lineHeight: 1.3 }}>
            {album.description.slice(0, 80)}{album.description.length > 80 ? "…" : ""}
          </p>
        )}
        <div className="text-subtle" style={{ fontSize: "0.75rem", marginTop: 8, display: "flex", gap: 12 }}>
          <span>{album.mediaCount} photo{album.mediaCount !== 1 ? "s" : ""}</span>
          {album.eventTitle && <span>📅 {album.eventTitle}</span>}
          {album.visibility === "groups" && <span>🔒 Group</span>}
        </div>
        {/* Actions */}
        <div style={{ display: "flex", gap: "var(--s-2)", marginTop: 10, flexWrap: "wrap" }}>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            hidden
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) setCover.mutate(file);
              e.target.value = "";
            }}
          />
          <button
            type="button"
            className="btn-tertiary"
            onClick={() => fileRef.current?.click()}
            disabled={setCover.isPending}
            style={{ fontSize: "0.75rem", padding: "4px 8px", minHeight: 26 }}
          >
            {setCover.isPending ? "…" : album.coverUrl ? "Change cover" : "Set cover"}
          </button>
          {canDelete && (
            <button
              type="button"
              className="btn-tertiary"
              onClick={() => {
                if (window.confirm(`Delete album "${album.title}"? This cannot be undone.`)) {
                  deleteAlbum.mutate();
                }
              }}
              disabled={deleteAlbum.isPending}
              style={{ fontSize: "0.75rem", padding: "4px 8px", minHeight: 26, color: "var(--c-danger)" }}
            >
              {deleteAlbum.isPending ? "Deleting…" : "Delete"}
            </button>
          )}
        </div>
        {(setCover.error || deleteAlbum.error) && (
          <p className="text-error" style={{ fontSize: "0.75rem", marginTop: 4 }}>
            {(setCover.error ?? deleteAlbum.error)?.message}
          </p>
        )}
      </div>
    </div>
  );
}

function CreateAlbumForm({
  familySlug,
  onCreated,
}: {
  familySlug: string;
  onCreated: () => void;
}) {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [visibility, setVisibility] = useState<"everyone" | "groups">("everyone");
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);

  // Fetch groups for the visibility picker
  const groupsQuery = useQuery<{ groups: Array<{ id: string; name: string }> }>({
    queryKey: ["groups", familySlug],
    queryFn: () => apiFetch(`/families/${familySlug}/groups`),
    enabled: visibility === "groups",
  });

  const create = useMutation({
    mutationFn: () =>
      apiFetch(`/families/${familySlug}/albums`, {
        method: "POST",
        body: JSON.stringify({
          title: title.trim(),
          description: description.trim() || undefined,
          visibility,
          groupIds: visibility === "groups" ? selectedGroups : undefined,
        }),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["albums", familySlug] });
      setTitle("");
      setDescription("");
      setVisibility("everyone");
      setSelectedGroups([]);
      onCreated();
    },
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (title.trim()) create.mutate();
      }}
      className="card stack"
    >
      <h3 style={{ margin: 0, fontSize: "1rem" }}>New Album</h3>
      <div>
        <label htmlFor="album-title">Title *</label>
        <input
          id="album-title"
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={200}
          required
          placeholder="e.g. Grandma's photo album, 1970s"
        />
      </div>
      <div>
        <label htmlFor="album-desc">Description</label>
        <input
          id="album-desc"
          type="text"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={2000}
          placeholder="Optional — who's in it, where and when"
        />
      </div>
      <div>
        <label htmlFor="album-visibility">Who can see this?</label>
        <select
          id="album-visibility"
          value={visibility}
          onChange={(e) => setVisibility(e.target.value as "everyone" | "groups")}
          style={{ width: "100%", fontSize: "0.9rem", padding: "8px", minHeight: 36 }}
        >
          <option value="everyone">Everyone in the family</option>
          <option value="groups">Specific group(s)</option>
        </select>
      </div>
      {visibility === "groups" && (
        <div>
          <label>Select groups</label>
          {groupsQuery.isLoading && <p className="text-muted" style={{ fontSize: "0.85rem" }}>Loading groups…</p>}
          {(groupsQuery.data?.groups ?? []).map((g) => (
            <label key={g.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: "0.9rem", padding: "4px 0" }}>
              <input
                type="checkbox"
                checked={selectedGroups.includes(g.id)}
                onChange={(e) => {
                  if (e.target.checked) setSelectedGroups([...selectedGroups, g.id]);
                  else setSelectedGroups(selectedGroups.filter((id) => id !== g.id));
                }}
                style={{ width: "auto", minHeight: 0 }}
              />
              {g.name}
            </label>
          ))}
          {(groupsQuery.data?.groups ?? []).length === 0 && !groupsQuery.isLoading && (
            <p className="text-muted" style={{ fontSize: "0.85rem" }}>No groups created yet. Create one in Settings first.</p>
          )}
        </div>
      )}
      {create.error && <p className="text-error">{create.error.message}</p>}
      <button type="submit" disabled={!title.trim() || create.isPending || (visibility === "groups" && selectedGroups.length === 0)}>
        {create.isPending ? "Creating…" : "Create Album"}
      </button>
    </form>
  );
}
